import { memo, useContext, useEffect, useState } from 'react';
import { ISideNavItem } from '../../types';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion';
import { cn } from '../../utils';
import { MobileNavigationContext } from '../../contexts';
import { Link, useLocation } from 'react-router-dom';

const subMenuVariants = {
  close: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.15 },
  },
  open: {
    opacity: 1,
    height: 'auto',
    transition: { duration: 0.15 },
  },
};

const MobileMenu: React.FC<{
  item: ISideNavItem;
  searchTerm?: string;
}> = memo(function MenuItem({ item, searchTerm }) {
  const { isOpen, setIsOpen } = useContext(MobileNavigationContext);
  const location = useLocation();
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const subMenuControls = useAnimationControls();

  const isActive = item.subMenuItems
    ? item.subMenuItems.some(subItem => location.pathname === subItem.path)
    : location.pathname === item.path;

  const toggleSubMenu = () => {
    setSubMenuOpen(prev => !prev);
  };

  useEffect(() => {
    if (subMenuOpen) {
      subMenuControls.start('open');
    } else {
      subMenuControls.start('close');
    }
  }, [subMenuOpen, subMenuControls]);

  useEffect(() => {
    if (!isOpen) {
      setSubMenuOpen(false);
    }
  }, [isOpen]);

  // Auto-expand submenu if search term matches any subitem or parent item
  useEffect(() => {
    if (searchTerm && item.subMenuItems?.length) {
      const query = searchTerm.trim().toLowerCase();
      const parentMatch = (item.title || '').toLowerCase().includes(query);
      const subMatch = item.subMenuItems.some(sub =>
        (sub.title || '').toLowerCase().includes(query),
      );
      if (parentMatch || subMatch) {
        setSubMenuOpen(true);
      }
    }
  }, [searchTerm, item.title, item.subMenuItems]);

  // Auto-expand on mount if current route matches subitem
  useEffect(() => {
    if (item.subMenuItems?.some(sub => location.pathname === sub.path)) {
      setSubMenuOpen(true);
    }
  }, [location.pathname, item.subMenuItems]);

  return (
    <div className="group relative">
      {item?.subMenu ? (
        <>
          <button
            type="button"
            onClick={toggleSubMenu}
            className={cn(
              'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all duration-100 outline-none text-slate-200 hover:bg-slate-800/80 hover:text-white',
              {
                'bg-slate-800 text-white font-bold': isActive,
              },
            )}
          >
            {item.icon && (
              <span className="[&>svg]:size-4 [&>svg]:stroke-current text-slate-400 shrink-0">
                {item.icon}
              </span>
            )}
            <span className="flex-1 text-left truncate tracking-wide">
              {item.title}
            </span>
            <ChevronRightIcon
              className={cn(
                'size-3.5 text-slate-400 transition-transform duration-200 shrink-0',
                {
                  'rotate-90 text-white': subMenuOpen,
                },
              )}
            />
          </button>
          <AnimatePresence initial={false}>
            {subMenuOpen && (
              <motion.ul
                initial="close"
                animate={subMenuControls}
                variants={subMenuVariants}
                exit="close"
                className="mt-0.5 ml-3 pl-2.5 border-l border-slate-800 space-y-0.5 overflow-hidden"
              >
                {item.subMenuItems?.map((subItem, idx) => {
                  const isSubActive = location.pathname === subItem.path;
                  return (
                    <li key={idx}>
                      <Link
                        to={subItem.path || '#'}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          'flex items-center gap-2 py-1.5 px-2 rounded-md text-xs font-medium transition-colors text-slate-400 hover:text-white hover:bg-slate-800/50',
                          {
                            'text-indigo-400 font-semibold bg-slate-800/80': isSubActive,
                          },
                        )}
                      >
                        <span className="truncate tracking-wide">
                          {subItem.title}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </motion.ul>
            )}
          </AnimatePresence>
        </>
      ) : (
        <Link
          to={item.path || '#'}
          onClick={() => setIsOpen(false)}
          className={cn(
            'flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all duration-100 text-slate-200 hover:bg-slate-800/80 hover:text-white',
            {
              'bg-slate-800 text-white font-bold': isActive,
            },
          )}
        >
          {item.icon && (
            <span className="[&>svg]:size-4 [&>svg]:stroke-current text-slate-400 shrink-0">
              {item.icon}
            </span>
          )}
          <span className="flex-1 text-left truncate tracking-wide">
            {item.title}
          </span>
        </Link>
      )}
    </div>
  );
});

export default MobileMenu;
